import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdvancedMarker, Map, useMap } from "@vis.gl/react-google-maps";
import { Dialog, DialogContent, IconButton, Paper, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import MapIcon from "@mui/icons-material/Map";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import MDBox from "components/MDBox";
import LinkedVehicleMarker from "./LinkedVehicleMarker";
import LinkedVehicleDetailsPanel from "./LinkedVehicleDetailsPanel";
import { ON_SITE_METERS, isLive, siteStatus } from "./linkedVehicleUtils";

const GOOGLE_MAP_ID = "d089c1a7f8831a36";
const DEFAULT_ZOOM = 15;
const TWEEN_MS = 1000;
const FIT_PADDING = 56;
const MAP_TYPE_STORAGE_KEY = "gateway-linked-map:type";

const readMapType = () => {
  try {
    return localStorage.getItem(MAP_TYPE_STORAGE_KEY) || "roadmap";
  } catch (error) {
    return "roadmap";
  }
};

// Tweens the marker toward `target` so updates glide instead of jumping.
function useTweenedPosition(target) {
  const lat = target?.lat;
  const lng = target?.lng;
  const [position, setPosition] = useState(target);
  const currentRef = useRef(target);
  const frameRef = useRef(null);

  useEffect(() => {
    if (lat == null || lng == null) return undefined;
    const from = currentRef.current ?? { lat, lng };
    if (from.lat === lat && from.lng === lng) return undefined;

    const start = performance.now();
    const step = (time) => {
      const t = Math.min((time - start) / TWEEN_MS, 1);
      const eased = 1 - (1 - t) ** 3;
      const next = {
        lat: from.lat + (lat - from.lat) * eased,
        lng: from.lng + (lng - from.lng) * eased,
      };
      currentRef.current = next;
      setPosition(next);
      if (t < 1) frameRef.current = requestAnimationFrame(step);
    };

    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [lat, lng]);

  return position;
}

function SitePin() {
  return (
    <MDBox
      sx={({ palette }) => ({
        color: palette.info.main,
        fontSize: "24px",
        lineHeight: 1,
        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
        pointerEvents: "none",
      })}
    >
      <i className="fa-solid fa-location-dot" />
    </MDBox>
  );
}

// @vis.gl/react-google-maps has no Circle/Polyline components, so these draw imperatively.
function SiteRadius({ mapId, center, color }) {
  const map = useMap(mapId);
  const lat = center?.lat;
  const lng = center?.lng;

  useEffect(() => {
    const maps = window.google?.maps;
    if (!map || !maps || lat == null || lng == null) return undefined;
    const circle = new maps.Circle({
      map,
      center: { lat, lng },
      radius: ON_SITE_METERS,
      strokeColor: color,
      strokeOpacity: 0.8,
      strokeWeight: 1.5,
      fillColor: color,
      fillOpacity: 0.08,
      clickable: false,
      zIndex: 0,
    });
    return () => circle.setMap(null);
  }, [map, lat, lng, color]);

  return null;
}

function SiteLink({ mapId, from, to, color }) {
  const map = useMap(mapId);
  const lineRef = useRef(null);
  const pathRef = useRef([]);
  pathRef.current = from && to ? [from, to] : [];

  useEffect(() => {
    const maps = window.google?.maps;
    if (!map || !maps) return undefined;
    const line = new maps.Polyline({
      map,
      path: pathRef.current,
      clickable: false,
      strokeOpacity: 0,
      zIndex: 1,
      icons: [
        {
          icon: { path: "M 0,-1 0,1", strokeColor: color, strokeOpacity: 0.9, strokeWeight: 2, scale: 3 },
          offset: "0",
          repeat: "12px",
        },
      ],
    });
    lineRef.current = line;
    return () => {
      line.setMap(null);
      lineRef.current = null;
    };
  }, [map, color]);

  // Runs every render so the line tracks the tweened marker frame by frame.
  useEffect(() => {
    lineRef.current?.setPath(pathRef.current);
  });

  return null;
}

function VehiclePin({ mapId, vehicle, now, selected, site, siteColor, onSelect }) {
  const position = useTweenedPosition(vehicle.position);
  const live = isLive(vehicle.lastUpdatedMs, now);
  const status = selected ? siteStatus(position, site) : null;

  return (
    <>
      {status && !status.onSite && (
        <SiteLink mapId={mapId} from={position} to={site.position} color={siteColor} />
      )}
      <AdvancedMarker
        position={position}
        title={vehicle.name}
        zIndex={selected ? 3 : 2}
        onClick={() => onSelect(vehicle.id)}
      >
        <LinkedVehicleMarker vehicle={vehicle} live={live} selected={selected} />
      </AdvancedMarker>
    </>
  );
}

// Lives inside <Map> so useMap(mapId) resolves to that specific instance.
function CameraController({ mapId, target, points, follow, paused, onUserMove, recenterSignal, fitSignal }) {
  const map = useMap(mapId);
  const programmaticRef = useRef(false);
  const handledRecenterRef = useRef(recenterSignal);
  const handledFitRef = useRef(fitSignal);
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const lat = target?.lat;
  const lng = target?.lng;

  useEffect(() => {
    if (!map) return undefined;
    const listeners = [
      map.addListener("dragstart", onUserMove),
      // Fit changes zoom programmatically; any other zoom change is the user.
      map.addListener("zoom_changed", () => {
        if (!programmaticRef.current) onUserMove();
      }),
      map.addListener("idle", () => {
        programmaticRef.current = false;
      }),
    ];
    return () => listeners.forEach((listener) => listener.remove());
  }, [map, onUserMove]);

  useEffect(() => {
    if (map && lat != null && lng != null && follow && !paused) map.panTo({ lat, lng });
  }, [map, lat, lng, follow, paused]);

  useEffect(() => {
    if (!map || recenterSignal === handledRecenterRef.current) return;
    handledRecenterRef.current = recenterSignal;
    if (lat != null && lng != null) map.panTo({ lat, lng });
  }, [map, recenterSignal, lat, lng]);

  useEffect(() => {
    if (!map || fitSignal === handledFitRef.current) return;
    handledFitRef.current = fitSignal;
    const maps = window.google?.maps;
    const fitPoints = pointsRef.current;
    if (!maps || fitPoints.length === 0) return;

    programmaticRef.current = true;
    if (fitPoints.length === 1) {
      map.setCenter(fitPoints[0]);
      map.setZoom(DEFAULT_ZOOM);
      return;
    }
    const bounds = new maps.LatLngBounds();
    fitPoints.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, FIT_PADDING);
  }, [map, fitSignal]);

  return null;
}

function MapControls({ mapTypeId, onToggleType, follow, onRecenter, onFit, onExpand, onClose }) {
  const isSatellite = mapTypeId === "hybrid";
  return (
    <Paper
      elevation={0}
      sx={({ palette, functions: { rgba } }) => ({
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1,
        display: "flex",
        gap: 0.5,
        p: 0.5,
        backgroundColor: rgba(palette.background.default, 0.85),
        border: `1px solid ${rgba(palette.text.main, 0.12)}`,
      })}
    >
      <Tooltip title={isSatellite ? "Map view" : "Satellite view"}>
        <IconButton size="small" onClick={onToggleType} aria-pressed={isSatellite}>
          {isSatellite ? <MapIcon fontSize="small" /> : <SatelliteAltIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={follow ? "Following vehicle" : "Recenter on vehicle"}>
        <IconButton size="small" onClick={onRecenter} color={follow ? "info" : "default"}>
          <MyLocationIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Fit vehicles and site">
        <IconButton size="small" onClick={onFit}>
          <ZoomOutMapIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {onExpand && (
        <Tooltip title="Expand map">
          <IconButton size="small" onClick={onExpand}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onClose && (
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}

function LinkedVehicleCanvas({
  mapId,
  initialCamera,
  vehicles,
  selected,
  site,
  now,
  onSelect,
  mapTypeId,
  follow,
  setFollow,
  recenterSignal,
  fitSignal,
  onToggleType,
  onRecenter,
  onFit,
  onExpand,
  onClose,
  onCameraSnapshot,
}) {
  const { palette } = useTheme();
  const [hovering, setHovering] = useState(false);
  const handleUserMove = useCallback(() => setFollow(false), [setFollow]);

  const points = useMemo(
    () => [...vehicles.map((vehicle) => vehicle.position), ...(site?.position ? [site.position] : [])],
    [vehicles, site]
  );

  return (
    <MDBox
      position="relative"
      width="100%"
      height="100%"
      onPointerEnter={() => setHovering(true)}
      onPointerLeave={() => setHovering(false)}
    >
      <Map
        id={mapId}
        mapId={GOOGLE_MAP_ID}
        defaultCenter={initialCamera.center}
        defaultZoom={initialCamera.zoom}
        mapTypeId={mapTypeId}
        gestureHandling="greedy"
        disableDefaultUI
        clickableIcons={false}
        onCameraChanged={(event) => onCameraSnapshot?.(event.detail)}
        style={{ width: "100%", height: "100%" }}
      >
        {site?.position && (
          <>
            <SiteRadius mapId={mapId} center={site.position} color={palette.info.main} />
            <AdvancedMarker position={site.position} title={site.name} zIndex={1}>
              <SitePin />
            </AdvancedMarker>
          </>
        )}
        {vehicles.map((vehicle) => (
          <VehiclePin
            key={vehicle.id}
            mapId={mapId}
            vehicle={vehicle}
            now={now}
            selected={vehicle.id === selected?.id}
            site={site}
            siteColor={palette.info.main}
            onSelect={onSelect}
          />
        ))}
        <CameraController
          mapId={mapId}
          target={selected?.position}
          points={points}
          follow={follow}
          paused={hovering}
          onUserMove={handleUserMove}
          recenterSignal={recenterSignal}
          fitSignal={fitSignal}
        />
      </Map>
      <MapControls
        mapTypeId={mapTypeId}
        onToggleType={onToggleType}
        follow={follow}
        onRecenter={onRecenter}
        onFit={onFit}
        onExpand={onExpand}
        onClose={onClose}
      />
    </MDBox>
  );
}

export default function LinkedVehicleMap({ vehicles, selected, onSelect, site, now }) {
  const [follow, setFollow] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [recenterSignal, setRecenterSignal] = useState(0);
  const [fitSignal, setFitSignal] = useState(0);
  const [mapTypeId, setMapTypeId] = useState(readMapType);
  const cameraRef = useRef({ center: selected?.position, zoom: DEFAULT_ZOOM });
  const selectedId = selected?.id;

  // Picking a vehicle (map, list, title chip, or Current Data link) follows it again.
  useEffect(() => {
    if (!selectedId) return;
    setFollow(true);
    setRecenterSignal((value) => value + 1);
  }, [selectedId]);

  const toggleMapType = () => {
    setMapTypeId((current) => {
      const next = current === "hybrid" ? "roadmap" : "hybrid";
      try {
        localStorage.setItem(MAP_TYPE_STORAGE_KEY, next);
      } catch (error) {
        console.error("persist map type", error);
      }
      return next;
    });
  };

  const snapshotCamera = useCallback((detail) => {
    cameraRef.current = { center: detail.center, zoom: detail.zoom };
  }, []);

  if (!selected) return null;

  const shared = {
    vehicles,
    selected,
    site,
    now,
    onSelect,
    mapTypeId,
    follow,
    setFollow,
    recenterSignal,
    fitSignal,
    onToggleType: toggleMapType,
    onRecenter: () => {
      setFollow(true);
      setRecenterSignal((value) => value + 1);
    },
    onFit: () => {
      setFollow(false);
      setFitSignal((value) => value + 1);
    },
  };

  return (
    <>
      <LinkedVehicleCanvas
        {...shared}
        mapId="gateway-linked-inline"
        initialCamera={{ center: selected.position, zoom: DEFAULT_ZOOM }}
        onExpand={() => setExpanded(true)}
        onCameraSnapshot={snapshotCamera}
      />

      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { height: "85vh" } }}
      >
        <DialogContent
          sx={{
            p: 0,
            height: "100%",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            overflowY: { xs: "auto", md: "hidden" },
          }}
        >
          {expanded && (
            <>
              <MDBox flex={1} minHeight={{ xs: "55vh", md: 0 }} position="relative">
                <LinkedVehicleCanvas
                  {...shared}
                  mapId="gateway-linked-expanded"
                  initialCamera={cameraRef.current}
                  onClose={() => setExpanded(false)}
                />
              </MDBox>
              <LinkedVehicleDetailsPanel
                vehicles={vehicles}
                selected={selected}
                onSelect={onSelect}
                site={site}
                now={now}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
